import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { clsx } from 'clsx'
import { api, ApiError, type BackupRow } from '../lib/api'
import { formatBytes, formatDateTime, formatRelative } from '../lib/format'
import { Spinner, Tag, ErrorNote, InfoNote, EmptyState } from '../components/ui/Primitives'
import { ConfirmDialog } from '../components/ui/ConfirmDialog'

export function BackupsTab({
  deploymentId, caps, isProtected, instanceName,
}: { deploymentId: string; caps: Set<string>; isProtected: boolean; instanceName: string }) {
  const qc = useQueryClient()
  const [banner, setBanner] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)
  const [restoring, setRestoring] = useState<string | null>(null)

  const canCreate = caps.has('instance.backups.create')
  const canRestore = caps.has('instance.backups.restore')

  const { data, isLoading } = useQuery({
    queryKey: ['backups', deploymentId],
    queryFn: () => api.get<BackupRow[]>(`/backups?deployment=${deploymentId}`),
  })

  const create = useMutation({
    mutationFn: (kind: 'FILES' | 'DATABASE' | 'FULL') =>
      api.post<{ checksum: string; sizeBytes: number; durationMs: number }>('/backups', { deploymentId, kind }),
    onSuccess: (r) => {
      setBanner({ kind: 'ok', text: `Backup creado: ${formatBytes(r.sizeBytes)} en ${(r.durationMs / 1000).toFixed(1)} s.` })
      void qc.invalidateQueries({ queryKey: ['backups', deploymentId] })
    },
    onError: (err) => setBanner({ kind: 'err', text: err instanceof Error ? err.message : 'Falló el backup' }),
  })

  const verify = useMutation({
    mutationFn: (id: string) => api.post<{ ok: boolean; detail: string }>(`/backups/${id}/verify`),
    onSuccess: (r) => {
      setBanner({ kind: r.ok ? 'ok' : 'err', text: r.detail })
      void qc.invalidateQueries({ queryKey: ['backups', deploymentId] })
    },
    onError: (err) => setBanner({ kind: 'err', text: err instanceof Error ? err.message : 'Falló la verificación' }),
  })

  const restore = useMutation({
    mutationFn: (v: { id: string; confirmName: string }) =>
      api.post<{ targetDir: string; note: string }>(`/backups/${v.id}/restore`, { confirmName: v.confirmName }),
    onSuccess: (r) => {
      setRestoring(null)
      setBanner({ kind: 'ok', text: r.note })
    },
    onError: (err) => {
      setBanner({ kind: 'err', text: err instanceof ApiError ? err.message : 'Falló la restauración' })
    },
  })

  if (isLoading) return <Spinner className="h-6 w-6" />

  return (
    <div className="space-y-4">
      {banner && (
        banner.kind === 'ok'
          ? <div className="rounded-lg border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm text-green-300">{banner.text}</div>
          : <ErrorNote>{banner.text}</ErrorNote>
      )}

      <div className="flex flex-wrap gap-2">
        {(['FULL', 'FILES', 'DATABASE'] as const).map((k) => (
          <button
            key={k}
            className="btn-ghost text-sm"
            disabled={!canCreate || create.isPending}
            onClick={() => create.mutate(k)}
          >
            {create.isPending && create.variables === k && <Spinner />}
            {k === 'FULL' ? 'Backup completo' : k === 'FILES' ? 'Solo archivos' : 'Solo base de datos'}
          </button>
        ))}
      </div>

      {isProtected && (
        <InfoNote>
          Esta instancia está protegida: <strong>sus backups no se pueden borrar desde el panel</strong>.
          Solo desaparecen cuando vence su política de retención.
        </InfoNote>
      )}

      {(data ?? []).length === 0 ? (
        <EmptyState title="Todavía no hay backups" hint="Creá uno con los botones de arriba." />
      ) : (
        <div className="card divide-y divide-slate-800/60 p-0">
          {(data ?? []).map((b) => (
            <div key={b.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
              <div className="min-w-0 flex-1">
                <p className="truncate font-mono text-xs">{b.location}</p>
                <p className="hint mt-0.5">
                  {formatDateTime(b.createdAt)} · {formatRelative(b.createdAt)}
                  {b.retainUntil && ` · vence ${formatRelative(b.retainUntil)}`}
                </p>
              </div>

              <Tag>{b.kind.toLowerCase()}</Tag>
              <span className={clsx(
                'rounded px-1.5 py-0.5 text-[11px] font-medium',
                b.status === 'VERIFIED' ? 'bg-green-500/10 text-green-400'
                  : b.status === 'SUCCESS' ? 'bg-blue-500/10 text-blue-400'
                  : b.status === 'FAILED' ? 'bg-red-500/10 text-red-400'
                  : 'bg-slate-500/10 text-slate-400',
              )}>
                {b.status === 'VERIFIED' ? 'verificado' : b.status.toLowerCase()}
              </span>
              <span className="hint w-20 text-right">{formatBytes(b.sizeBytes)}</span>

              <div className="flex gap-1.5">
                <button
                  className="btn-ghost px-2 py-1 text-xs"
                  disabled={verify.isPending}
                  onClick={() => verify.mutate(b.id)}
                  title="Descifra el backup y compara el checksum con el que se guardó"
                >
                  Verificar
                </button>
                <a className="btn-ghost px-2 py-1 text-xs" href={`/api/v1/backups/${b.id}/download`}>
                  Descargar
                </a>
                <button
                  className="btn-ghost px-2 py-1 text-xs"
                  disabled={!canRestore || b.status === 'FAILED'}
                  onClick={() => setRestoring(b.id)}
                >
                  Restaurar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="hint">
        Los backups se guardan cifrados. Sin la clave <code className="font-mono">BACKUP_KEY</code> del
        servidor, el archivo descargado no sirve de nada — incluido para quien se lleve la base del panel.
      </p>

      <ConfirmDialog
        open={Boolean(restoring)}
        title="Restaurar backup"
        description={`Se va a descifrar, verificar el checksum y extraer en un directorio NUEVO al lado del original. No se pisa lo que está corriendo.`}
        confirmName={instanceName}
        confirmLabel="Restaurar"
        busy={restore.isPending}
        onCancel={() => setRestoring(null)}
        onConfirm={(typed) => restoring && typed && restore.mutate({ id: restoring, confirmName: typed })}
      />
    </div>
  )
}
